/**
 * How bytes reach a printer.
 *
 * The same seam shape `domain/messaging` and `domain/payments` use on the
 * server, and for the same reason: what a transport is differs per deployment,
 * and the alternative is a device API compiled into the code that decides what
 * a receipt says.
 *
 * **`pos-print` produced correct, tested bytes that nothing sent anywhere.** It
 * was imported by no client — the package was in `apps/pos`'s dependencies and
 * absent from its source. This is the missing half.
 *
 * **What is verified and what is not**, said plainly rather than left for
 * somebody to discover: the queue, the claim, the encoding, the settle call and
 * the retry are all exercised. Whether a Star or an Epson actually prints what
 * `web-bluetooth.ts` writes to it is **not** — there is no printer here, and
 * `README.md` records what happened the last time this repo built a surface
 * without ground truth. The `log` transport exists so the half that can be
 * verified is verified, and so the half that cannot is obvious.
 */

/**
 * The slice of Web Bluetooth this file uses, declared here rather than pulled
 * in as `@types/web-bluetooth`.
 *
 * A new dependency is a last resort, and this is four members of an API that is
 * still a draft spec — writing down exactly what is used documents the surface
 * a different printer model would have to satisfy, which a wholesale type
 * package does not.
 */
type BluetoothRemoteGATTCharacteristic = {
  writeValueWithoutResponse(value: BufferSource): Promise<void>;
};

type BluetoothNavigator = {
  bluetooth: {
    requestDevice(options: { filters: { services: number[] }[] }): Promise<{
      gatt?: {
        connect(): Promise<{
          getPrimaryService(service: number): Promise<{
            getCharacteristic(characteristic: number): Promise<BluetoothRemoteGATTCharacteristic>;
          }>;
        }>;
      };
      addEventListener(type: string, listener: () => void): void;
    }>;
  };
};

export type PrintTransport = {
  readonly code: string;
  /** Whether bytes reach a device rather than the console. */
  readonly isRealDevice: boolean;
  send(printerId: string, bytes: Uint8Array): Promise<void>;
};

/**
 * Delivery to the browser console.
 *
 * Real delivery to the only recipient it can reach, exactly as the server's
 * `log` message channel is. It is what makes the pipeline testable end to end
 * without hardware, and `isRealDevice: false` is what says a merchant is not
 * getting a receipt from it.
 */
export const logPrintTransport: PrintTransport = {
  code: "log",
  isRealDevice: false,
  async send(printerId, bytes) {
    console.info(
      `[print:${printerId}] ${bytes.length} bytes`,
      // Hex, because these are ESC/POS control codes and a string view of them
      // is mojibake that hides the escapes somebody is trying to check.
      Array.from(bytes.slice(0, 64), (b) => b.toString(16).padStart(2, "0")).join(" "),
    );
  },
};

/**
 * A BLE thermal printer over Web Bluetooth.
 *
 * **Not available inside the Capacitor shell.** Android's System WebView does
 * not implement Web Bluetooth — `navigator.bluetooth` is simply absent, where
 * Chrome for Android has it. So this transport works in a browser on a tablet
 * and *not* in the packaged till, which is the opposite of what one would
 * assume. Closing it needs a native plugin bridging Android's
 * Bluetooth stack: the peripheral spike `10-build-plan.md` § Phase 2 asks for,
 * still unrun for want of a printer.
 *
 * **Unverified against hardware.** The GATT service and characteristic below
 * are the ones the common Chinese BLE printer modules expose and that most
 * ESC/POS libraries target, but no printer was available to confirm it, and a
 * different model will need a different pair. Treated as configuration the
 * moment somebody has one in front of them.
 *
 * Chunked at 180 bytes because a BLE characteristic write has an MTU well under
 * a receipt, and a write that exceeds it fails silently on some stacks rather
 * than erroring — which would look exactly like a printer that is off.
 */
const BLE_SERVICE = 0x18f0;
const BLE_CHARACTERISTIC = 0x2af1;
const BLE_CHUNK = 180;

function hasWebBluetooth(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as unknown as Partial<BluetoothNavigator>).bluetooth !== undefined
  );
}

export function webBluetoothTransport(): PrintTransport {
  const connected = new Map<string, BluetoothRemoteGATTCharacteristic>();

  return {
    code: "web-bluetooth",
    isRealDevice: true,
    async send(printerId, bytes) {
      // Checked here rather than at startup because the failure has to reach
      // the merchant, and the print log is where they look. Three attempts of
      // a sentence beats three attempts of a `TypeError`.
      if (!hasWebBluetooth()) {
        throw new Error("This device has no Web Bluetooth. It cannot reach a Bluetooth printer.");
      }

      let characteristic = connected.get(printerId);

      if (!characteristic) {
        // `requestDevice` must be called from a user gesture, which is why
        // pairing is a button on the printers screen rather than something the
        // worker does when it first has something to print.
        const device = await (navigator as unknown as BluetoothNavigator).bluetooth.requestDevice({
          filters: [{ services: [BLE_SERVICE] }],
        });
        const server = await device.gatt?.connect();
        if (!server) throw new Error("The printer would not connect.");
        const service = await server.getPrimaryService(BLE_SERVICE);
        characteristic = await service.getCharacteristic(BLE_CHARACTERISTIC);
        connected.set(printerId, characteristic);

        device.addEventListener("gattserverdisconnected", () => connected.delete(printerId));
      }

      for (let at = 0; at < bytes.length; at += BLE_CHUNK) {
        await characteristic.writeValueWithoutResponse(bytes.slice(at, at + BLE_CHUNK));
      }
    },
  };
}

let transport: PrintTransport = logPrintTransport;

export function setPrintTransport(next: PrintTransport): void {
  transport = next;
}

export function printTransport(): PrintTransport {
  return transport;
}
